import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  getClassesByTeacherId,
  getTeachersBySchoolId,
  removeFromSharedClass,
  removeOrReplaceClassOwner,
  replaceClassCreator
} from "../services/TeacherAPIClient";
import { getDistinctClassGradeLabel } from "../utils/classDisplay";
import "./TeacherRemoveTeacherAssignmentModal.css";

const normaliseText = (value) => String(value || "").trim().replace(/\s+/g, " ");

const getTeacherId = (teacher) =>
  teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId || teacher?.teacher_id;

const getClassId = (classItem) => classItem?.id || classItem?._id || classItem?.classid || classItem?.classId;

const getClassOwnerId = (classItem) =>
  classItem?.classOwnerId || classItem?.class_owner_id || classItem?.ownerId || classItem?.owner_id;

const getClassCreatorId = (classItem) =>
  classItem?.teacherId || classItem?.teacher_id || classItem?.teacherid || classItem?.creatorId || classItem?.creator_id;

const getAssociationRoles = (assignment) => {
  const value = assignment?.association || assignment?.associations || [];
  const associations = Array.isArray(value) ? value : String(value).split(/[\s,]+/);

  return associations.map((association) => String(association).trim().toLowerCase()).filter(Boolean);
};

const normaliseTeacher = (teacher) => ({
  id: getTeacherId(teacher),
  name: teacher?.name || teacher?.fullName || teacher?.displayName || "Unnamed teacher",
  detail: teacher?.roleLabel || teacher?.role || teacher?.email || "Teacher"
});

const normaliseClass = (classItem, teachersById, countryGrades) => {
  const ownerId = getClassOwnerId(classItem);
  const creatorId = getClassCreatorId(classItem);
  const ownerName =
    teachersById.get(ownerId)?.name ||
    classItem?.teacherName ||
    classItem?.ownerName ||
    classItem?.classOwnerName ||
    "Class teacher";
  const name = classItem?.name || classItem?.className || "Unnamed class";
  const year = classItem?.academicYear || classItem?.academic_year || classItem?.academicyear || "Current year";
  const grade = getDistinctClassGradeLabel(classItem, name, countryGrades);

  return {
    ...classItem,
    id: getClassId(classItem),
    name,
    ownerId,
    creatorId,
    ownerName,
    detail: [grade, `${ownerName} owns this class`, year].filter(Boolean).join(" - "),
    searchText: [name, grade, ownerName, year].filter(Boolean).join(" ").toLowerCase()
  };
};

const normaliseAssignedClasses = (assignments, teachers, selectedTeacherId, countryGrades) => {
  const classesById = new Map();
  const teachersById = new Map(teachers.map((teacher) => [teacher.id, teacher]));

  (Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
    const classItem = normaliseClass(assignment?.class || assignment, teachersById, countryGrades);
    if (!classItem.id) return;

    const existing = classesById.get(classItem.id);
    const roles = new Set([...(existing?.roles || []), ...getAssociationRoles(assignment)]);

    classesById.set(classItem.id, {
      ...(existing || classItem),
      roles: [...roles],
      isShared: roles.has("shared"),
      isOwned:
        roles.has("owned") ||
        roles.has("owner") ||
        classItem.ownerId === selectedTeacherId,
      isCreated:
        roles.has("created") ||
        roles.has("creator") ||
        classItem.creatorId === selectedTeacherId
    });
  });

  return [...classesById.values()].sort((firstClass, secondClass) => firstClass.name.localeCompare(secondClass.name));
};

function TeacherRemoveTeacherAssignmentModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  teacherid,
  currentTeacherName,
  prefill = null,
  onRequestAccessForTeacher = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [assignmentTarget, setAssignmentTarget] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teacherName, setTeacherName] = useState("");
  const [selectedTeacherRow, setSelectedTeacherRow] = useState(null);
  const [className, setClassName] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [newOwnerChoice, setNewOwnerChoice] = useState("");
  const [secondTeacherName, setSecondTeacherName] = useState("");
  const [selectedNewOwnerTeacher, setSelectedNewOwnerTeacher] = useState(null);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefilledClassName = prefill?.className || "";

  useEffect(() => {
    if (!show) return;

    const prefilledTarget = prefill?.assignmentTarget
      ? String(prefill.assignmentTarget).toLowerCase() === "me" ? "Me" : "A colleague"
      : "";

    setStage("form");
    setAssignmentTarget(prefilledTarget);
    setTeachers([]);
    setClasses([]);
    setTeacherName(prefill?.teacherName || (prefilledTarget === "Me" ? currentTeacherName || "" : ""));
    setSelectedTeacherRow(null);
    setClassName(prefill?.className || "");
    setSelectedClass(null);
    setSecondTeacherName(prefill?.secondTeacherName || "");
    setSelectedNewOwnerTeacher(null);
    setNewOwnerChoice(prefill?.secondTeacherName ? "Choose another teacher" : "");
    setIsSubmitting(false);
    setError("");

    if (!schoolid) {
      setError("Teachers and classes could not be loaded. Please try again.");
      return;
    }

    setIsLoadingContext(true);
    getTeachersBySchoolId(schoolid)
      .then((teachersResponse) => {
        const normalisedTeachers = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((teacher) => teacher.id)
          .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));
        const prefilledTeacher = normalisedTeachers.find(
          (teacher) => normaliseText(teacher.name).toLowerCase() === normaliseText(prefill?.teacherName || "").toLowerCase()
        );
        const prefilledNewOwner = normalisedTeachers.find(
          (teacher) => normaliseText(teacher.name).toLowerCase() === normaliseText(prefill?.secondTeacherName || "").toLowerCase()
        );

        setTeachers(normalisedTeachers);

        if (prefilledTarget === "Me" && teacherid) {
          setSelectedTeacherRow({ id: teacherid, name: currentTeacherName, detail: "Current teacher" });
        } else if (prefilledTeacher) {
          setSelectedTeacherRow(prefilledTeacher);
          setTeacherName(prefilledTeacher.name);
        }

        if (prefilledNewOwner) {
          setSelectedNewOwnerTeacher(prefilledNewOwner);
          setSecondTeacherName(prefilledNewOwner.name);
        }
      })
      .catch((loadError) => {
        console.error("Error loading remove-assignment context:", loadError);
        setError("Teachers and classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingContext(false);
      });
  }, [show, schoolid, teacherid, prefill, currentTeacherName]);

  const selectedTeacherId = selectedTeacherRow?.id;

  useEffect(() => {
    if (!show || !selectedTeacherId) {
      setClasses([]);
      setIsLoadingClasses(false);
      return;
    }

    if (isLoadingContext) {
      setIsLoadingClasses(true);
      return;
    }

    let isCurrentRequest = true;
    setClasses([]);
    setSelectedClass(null);
    setIsLoadingClasses(true);
    setError("");

    getClassesByTeacherId(selectedTeacherId)
      .then((assignments) => {
        if (!isCurrentRequest) return;

        const assignedClasses = normaliseAssignedClasses(assignments, teachers, selectedTeacherId, countryGrades)
          .filter((classItem) => classItem.active !== false);
        const prefilledClass = assignedClasses.find(
          (classItem) => normaliseText(classItem.name).toLowerCase() === normaliseText(prefilledClassName).toLowerCase()
        );

        setClasses(assignedClasses);
        if (prefilledClass) {
          setSelectedClass(prefilledClass);
          setClassName(prefilledClass.name);
        }
      })
      .catch((loadError) => {
        if (!isCurrentRequest) return;
        console.error("Error loading teacher classes:", loadError);
        setError("Classes for this teacher could not be loaded. Please try again.");
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoadingClasses(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [show, selectedTeacherId, prefilledClassName, isLoadingContext, teachers, countryGrades]);

  const selectedTeacher = assignmentTarget === "Me" ? currentTeacherName || teacherName : teacherName;
  const needsReplacement = Boolean(selectedClass?.isOwned || selectedClass?.isCreated);
  const canTakeOwnership = Boolean(teacherid && selectedTeacherId && teacherid !== selectedTeacherId);
  const canContinue = Boolean(
    selectedTeacher.trim() &&
    selectedTeacherId &&
    selectedClass &&
    className.trim() &&
    !isLoadingClasses &&
    !isSubmitting
  );
  const ownerName = newOwnerChoice === "I will take ownership"
    ? currentTeacherName || "You"
    : selectedNewOwnerTeacher?.name || secondTeacherName;
  const filteredTeachers = teachers.filter((teacher) =>
    !teacherName.trim() || [teacher.name, teacher.detail].some((value) =>
      normaliseText(value).toLowerCase().includes(normaliseText(teacherName).toLowerCase())
    )
  );
  const filteredNewOwnerTeachers = teachers.filter((teacher) =>
    teacher.id !== selectedTeacherId &&
    (!canTakeOwnership || teacher.id !== teacherid) &&
    (!secondTeacherName.trim() || [teacher.name, teacher.detail].some((value) =>
      normaliseText(value).toLowerCase().includes(normaliseText(secondTeacherName).toLowerCase())
    ))
  );
  const filteredClasses = classes.filter((classItem) =>
    !className.trim() || classItem.searchText.includes(normaliseText(className).toLowerCase())
  );

  const handleAssignmentTargetChange = (target) => {
    setAssignmentTarget(target);
    setClasses([]);
    setClassName("");
    setSelectedClass(null);
    setNewOwnerChoice("");
    setSecondTeacherName("");
    setSelectedNewOwnerTeacher(null);
    setError("");
    if (target === "Me") {
      setTeacherName(currentTeacherName || "");
      setSelectedTeacherRow({ id: teacherid, name: currentTeacherName || "Me", detail: "Current teacher" });
    } else {
      setTeacherName("");
      setSelectedTeacherRow(null);
    }
  };

  const handleNewOwnerChoiceChange = (choice) => {
    setNewOwnerChoice(choice);
    setError("");
    if (choice === "I will take ownership") {
      setSecondTeacherName("");
      setSelectedNewOwnerTeacher(null);
    }
  };

  const resetForAnother = () => {
    setStage("form");
    setAssignmentTarget("");
    setTeacherName("");
    setSelectedTeacherRow(null);
    setClassName("");
    setSelectedClass(null);
    setNewOwnerChoice("");
    setSecondTeacherName("");
    setSelectedNewOwnerTeacher(null);
    setError("");
  };

  const handleRemoveAssignment = async () => {
    if (!selectedClass || !selectedTeacherId || isSubmitting) {
      return;
    }

    const hasRemovableAssociation = selectedClass.isShared || selectedClass.isOwned || selectedClass.isCreated;
    const newOwnerId = newOwnerChoice === "I will take ownership"
      ? teacherid
      : selectedNewOwnerTeacher?.id;

    if (!hasRemovableAssociation) {
      setError("This teacher's class assignment could not be identified. Refresh and try again.");
      return;
    }

    if (needsReplacement && !newOwnerId) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      if (selectedClass.isShared) {
        await removeFromSharedClass(selectedClass.id, selectedTeacherId);
      }
      if (selectedClass.isOwned) {
        await removeOrReplaceClassOwner(selectedClass.id, newOwnerId);
      }
      if (selectedClass.isCreated) {
        await replaceClassCreator(selectedClass.id, newOwnerId);
      }
      setStage("success");
    } catch (submitError) {
      console.error("Error removing teacher assignment:", submitError);
      setError("The teacher could not be removed from this class. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (!canContinue) return;

    if (needsReplacement) {
      setStage("owner");
      return;
    }

    handleRemoveAssignment();
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: className || selectedTeacher || "",
        message: "Teacher assignment removed."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderChoiceCard = (name, value, detail, selected, onSelect) => (
    <button
      key={value}
      type="button"
      className={`btn w-100 text-start border rounded mb-2 p-3 ${selected ? "btn-primary bg-primary bg-opacity-10 text-dark border-primary" : "btn-light bg-white"}`}
      onClick={() => onSelect(value)}
    >
      <span className="d-flex align-items-start gap-2">
        <Form.Check
          type="radio"
          name={name}
          checked={selected}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="mt-0"
        />
        <span>
          <strong className="d-block">{value}</strong>
          <span className="text-muted small">{detail}</span>
        </span>
      </span>
    </button>
  );

  const renderResultRow = (option, selected, onSelect) => (
    <button
      key={option.id || option.name}
      type="button"
      className={`btn w-100 text-start border rounded mb-2 p-3 ${selected ? "btn-primary bg-primary bg-opacity-10 text-dark border-primary" : "btn-light bg-white"}`}
      onClick={() => onSelect(option)}
      aria-label={`${selected ? "Selected" : "Select"} ${option.name}`}
      aria-pressed={selected}
    >
      <span className="d-flex align-items-start gap-2">
        <Form.Check
          type="radio"
          checked={selected}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="mt-0"
        />
        <span>
          <strong className="d-block">{option.name}</strong>
          <span className="text-muted small">{option.detail}</span>
        </span>
      </span>
    </button>
  );

  const renderTeacherResults = () => {
    if (assignmentTarget === "Me") {
      return <p className="text-muted small">Your own assignment is selected.</p>;
    }

    if (isLoadingContext) {
      return <div className="text-muted small d-flex gap-2 align-items-center"><Spinner animation="border" size="sm" /> Loading teachers...</div>;
    }

    if (selectedTeacherRow) {
      return renderResultRow(selectedTeacherRow, true, () => setSelectedTeacherRow(null));
    }

    if (!teacherName.trim()) {
      return <p className="text-muted small">Start typing a teacher name.</p>;
    }

    if (filteredTeachers.length < 1) {
      return <p className="text-muted small">No matching teachers found.</p>;
    }

    return filteredTeachers.slice(0, 5).map((option) =>
      renderResultRow(option, false, () => {
        setSelectedTeacherRow(option);
        setTeacherName(option.name);
        setClassName("");
        setSelectedClass(null);
        setNewOwnerChoice("");
        setSecondTeacherName("");
        setSelectedNewOwnerTeacher(null);
        setError("");
      })
    );
  };

  const renderClassResults = () => {
    if (!selectedTeacherId) {
      return <p className="text-muted small">Choose the teacher first.</p>;
    }

    if (isLoadingClasses) {
      return <div className="text-muted small d-flex gap-2 align-items-center"><Spinner animation="border" size="sm" /> Loading classes...</div>;
    }

    if (selectedClass) {
      return renderResultRow(selectedClass, true, () => setSelectedClass(null));
    }

    if (!className.trim() && classes.length < 1) {
      return <p className="text-muted small">This teacher does not have access to any classes.</p>;
    }

    if (!className.trim()) {
      return <p className="text-muted small">Start typing a class name.</p>;
    }

    if (filteredClasses.length < 1) {
      return <p className="text-muted small">No matching classes found.</p>;
    }

    return filteredClasses.slice(0, 5).map((option) =>
      renderResultRow(option, false, () => {
        setSelectedClass(option);
        setClassName(option.name);
        setNewOwnerChoice("");
        setSecondTeacherName("");
        setSelectedNewOwnerTeacher(null);
        setError("");
      })
    );
  };

  const renderNewOwnerResults = () => {
    if (isLoadingContext) {
      return <div className="text-muted small d-flex gap-2 align-items-center"><Spinner animation="border" size="sm" /> Loading teachers...</div>;
    }

    if (selectedNewOwnerTeacher) {
      return renderResultRow(selectedNewOwnerTeacher, true, () => setSelectedNewOwnerTeacher(null));
    }

    if (!secondTeacherName.trim()) {
      return <p className="text-muted small">Start typing a teacher name.</p>;
    }

    if (filteredNewOwnerTeachers.length < 1) {
      return <p className="text-muted small">No matching teachers found.</p>;
    }

    return filteredNewOwnerTeachers.slice(0, 5).map((option) =>
      renderResultRow(option, false, () => {
        setSelectedNewOwnerTeacher(option);
        setSecondTeacherName(option.name);
        setError("");
      })
    );
  };

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-remove-assignment-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Remove Teacher From a Class</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">Choose whose class access should be removed.</p>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Whose assignment should be removed?</Form.Label>
              {renderChoiceCard(
                "assignmentTarget",
                "A colleague",
                "Search for another teacher",
                assignmentTarget === "A colleague",
                handleAssignmentTargetChange
              )}
              {renderChoiceCard(
                "assignmentTarget",
                "Me",
                "Remove my own access",
                assignmentTarget === "Me",
                handleAssignmentTargetChange
              )}
            </Form.Group>

            <h3 className="h5 fw-bold">Which teacher?</h3>
            <Form.Group className="mb-3" controlId="removeAssignmentTeacher">
              <Form.Label className="fw-bold small">Search teacher</Form.Label>
              <Form.Control
                type="search"
                value={selectedTeacher}
                onChange={(event) => {
                  setTeacherName(event.target.value);
                  setSelectedTeacherRow(null);
                  setClasses([]);
                  setClassName("");
                  setSelectedClass(null);
                  setNewOwnerChoice("");
                  setSecondTeacherName("");
                  setSelectedNewOwnerTeacher(null);
                  setError("");
                }}
                disabled={assignmentTarget === "Me" && Boolean(currentTeacherName)}
              />
            </Form.Group>
            <div className="mb-4">
              {renderTeacherResults()}
            </div>

            <h3 className="h5 fw-bold">Which class?</h3>
            <Form.Group className="mb-3" controlId="removeAssignmentClass">
              <Form.Label className="fw-bold small">Search class</Form.Label>
              <Form.Control
                type="search"
                value={className}
                onChange={(event) => {
                  setClassName(event.target.value);
                  setSelectedClass(null);
                  setNewOwnerChoice("");
                  setSecondTeacherName("");
                  setSelectedNewOwnerTeacher(null);
                  setError("");
                }}
                disabled={!selectedTeacherId || isLoadingClasses}
              />
            </Form.Group>
            <div>
              {renderClassResults()}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="text-muted me-auto">{canContinue ? `${selectedTeacher} has access to ${className}.` : "Choose teacher and class."}</span>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={handleContinue} disabled={!canContinue}>
              {isSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
              Next
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "owner" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Choose New Class Owner</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">
              {selectedClass?.isOwned
                ? `${selectedTeacher} owns this class, so ownership must move before they are removed.`
                : `${selectedTeacher} created this class, so another teacher must take over before they are removed.`}
            </p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning">
              <strong>Who should own {className}?</strong>
              <div className="mt-2">Choose who will own the class after {selectedTeacher} is removed from this class.</div>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">New owner</Form.Label>
              {canTakeOwnership && renderChoiceCard(
                  "newOwnerChoice",
                  "I will take ownership",
                  `${className} will move to me`,
                  newOwnerChoice === "I will take ownership",
                  handleNewOwnerChoiceChange
                )}
              {renderChoiceCard(
                "newOwnerChoice",
                "Choose another teacher",
                "Search for a different class owner",
                newOwnerChoice === "Choose another teacher",
                handleNewOwnerChoiceChange
              )}
            </Form.Group>

            {newOwnerChoice === "Choose another teacher" && (
              <Form.Group controlId="removeAssignmentNewOwner">
                <Form.Label className="fw-bold small">Search teacher</Form.Label>
                <Form.Control
                  type="search"
                  value={secondTeacherName}
                  onChange={(event) => {
                    setSecondTeacherName(event.target.value);
                    setSelectedNewOwnerTeacher(null);
                  }}
                />
                <div className="mt-2">
                  {renderNewOwnerResults()}
                </div>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <span className="text-muted me-auto">{newOwnerChoice ? "New owner selected." : "Choose a new owner."}</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
            <Button
              variant="primary"
              onClick={handleRemoveAssignment}
              disabled={isSubmitting || !newOwnerChoice || (newOwnerChoice === "Choose another teacher" && !selectedNewOwnerTeacher)}
            >
              {isSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
              Transfer and remove
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "success" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Teacher Removed</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {needsReplacement && (
              <p className="text-muted">{newOwnerChoice === "I will take ownership" ? "You are now the owner" : `${ownerName} is now the owner`} of {className}.</p>
            )}
            <Alert variant="success" className="d-flex gap-3 align-items-start">
              <FaCheckCircle className="mt-1" aria-hidden="true" />
              <div>
                <strong>{selectedTeacher} has been removed from {className}.</strong>
                <div className="text-muted">
                  {needsReplacement
                    ? `${newOwnerChoice === "I will take ownership" ? "You are now owner" : `${ownerName} is now owner`}, ${selectedTeacher} removed from ${className}.`
                    : `${selectedTeacher} no longer has access to ${className}.`}
                </div>
              </div>
            </Alert>
            <div className="border rounded p-3">
              <strong>What next?</strong>
              <div className="text-muted">
                {embedded
                  ? "Done returns to SENSAI. Remove another assignment repeats the action."
                  : "Done returns to Help Centre home. Remove another assignment repeats the action."}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="text-muted me-auto">Task complete.</span>
            <Button variant="outline-secondary" onClick={resetForAnother}>Remove another assignment</Button>
            {onRequestAccessForTeacher && (
              <Button
                variant="outline-secondary"
                onClick={() => onRequestAccessForTeacher({
                  className,
                  teacherName: selectedNewOwnerTeacher?.name || ""
                })}
              >
                Request access for teacher
              </Button>
            )}
            <Button variant="primary" onClick={handleDone}>Done</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherRemoveTeacherAssignmentModal;
